import React, { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { getTimeOnTask, isOk } from '../../helpers/trainingApi'
import { viz } from '../../../pages/userPages/dashboard/managerOverview/overviewTheme'

// Median bar against a tick at the estimate, both on one minutes scale
// shared by every row so the rows can be compared.
const TimeBar = ({ median, estimate, max }) => {
  const pct = (value) => `${Math.min(100, (value / max) * 100)}%`
  return (
    <Tooltip
      title={`Median ${median} min${estimate ? `, estimated ${estimate} min` : ''}`}
      arrow
    >
      <Box
        role="img"
        aria-label={`Median ${median} minutes${estimate ? `, estimated ${estimate} minutes` : ''}`}
        sx={{ position: 'relative', height: 18, minWidth: 120 }}
      >
        <Box sx={{ position: 'absolute', left: 0, right: 0, top: 8, height: 2, bgcolor: viz.grid }} />
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: 4,
            height: 10,
            width: pct(median),
            bgcolor: viz.series,
            borderRadius: '0 4px 4px 0',
          }}
        />
        {estimate ? (
          <Box sx={{ position: 'absolute', left: pct(estimate), top: 0, height: 18, width: 2, bgcolor: viz.ink }} />
        ) : null}
      </Box>
    </Tooltip>
  )
}

// How long staff actually spend on each e-learning course against the time
// the course says it takes. Renders nothing until there are completions.
const TimeOnTaskCard = () => {
  const [rows, setRows] = useState([])

  useEffect(() => {
    let active = true
    getTimeOnTask().then((res) => {
      if (active && isOk(res) && Array.isArray(res.data)) setRows(res.data)
    })
    return () => {
      active = false
    }
  }, [])

  if (rows.length === 0) return null

  const max = Math.max(...rows.flatMap((row) => [row.medianMinutes ?? 0, row.estimatedMinutes ?? 0]), 1)

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack gap={1}>
        <Box>
          <Typography variant="title3" fontWeight={700}>
            Time spent on e-learning
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The median active learning time of staff who completed each course, against the
            estimated time. The bar is the median; the dark line marks the estimate.
          </Typography>
        </Box>
        <Box overflow="auto">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <b>Course</b>
                </TableCell>
                <TableCell align="right">
                  <b>Completions</b>
                </TableCell>
                <TableCell align="right">
                  <b>Median</b>
                </TableCell>
                <TableCell align="right">
                  <b>Estimated</b>
                </TableCell>
                <TableCell sx={{ width: '30%', display: { xs: 'none', sm: 'table-cell' } }}>
                  <span style={{ position: 'absolute', left: -9999 }}>Median against estimate</span>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={`${row.code}-${row.version}-${row.pathwayId}`}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {row.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[row.pathwayTitle !== row.title ? row.pathwayTitle : null, `${row.code} v${row.version}`]
                        .filter(Boolean)
                        .join(' · ')}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{row.completions}</TableCell>
                  <TableCell align="right">{row.medianMinutes} min</TableCell>
                  <TableCell align="right">
                    {row.estimatedMinutes ? `${row.estimatedMinutes} min` : 'Not set'}
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    <TimeBar median={row.medianMinutes} estimate={row.estimatedMinutes} max={max} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Stack>
    </Paper>
  )
}

export default TimeOnTaskCard
